/**
 * Central appearance / theme system for the App Factory frontend.
 *
 * The visual tokens live in `styles.css`, keyed off the document root:
 * `:root[data-theme="dark"]` (and, ready for future use, `[data-accent]`,
 * `[data-density]`, `[data-plain]`). This module is the single source of truth
 * for *which* attribute is stamped on `<html>`, how the choice persists, and
 * how it tracks the OS setting.
 *
 * Design goals:
 *  - No hardcoding in components. UI reads {@link THEME_OPTIONS}; adding a theme
 *    is one entry here plus a matching `:root[data-theme="…"]` block in the CSS.
 *  - Scalable to more appearance axes (accent, density) — the paint/persist
 *    plumbing is generic over a document-root data-attribute.
 *  - Framework-light: a tiny external store so any component can subscribe via
 *    {@link useThemeMode} without threading a Context through the tree, and so
 *    the initial paint can run before React mounts (no flash of wrong theme).
 */
import { useSyncExternalStore } from 'react';

/** A user-selectable theme preference. `system` follows the OS setting live. */
export type ThemeMode = 'light' | 'dark' | 'system';

/** The concrete theme actually painted (what `system` resolves to). */
export type ResolvedTheme = 'light' | 'dark';

export interface ThemeOption {
  id: ThemeMode;
  label: string;
  /** 24×24 SVG path data, rendered through the shared `Icon` component. */
  icon: string;
}

/**
 * The registry the UI renders from. Order here is the order shown in menus.
 * Add a new mode by appending an entry (and, if it introduces new tokens, a
 * `:root[data-theme="…"]` block in styles.css) — no component edits required.
 */
export const THEME_OPTIONS: ThemeOption[] = [
  {
    id: 'light',
    label: 'Light',
    icon: 'M12 3v1.5M12 19.5V21M4.5 12H3M21 12h-1.5M5.6 5.6l1.1 1.1M17.3 17.3l1.1 1.1M18.4 5.6l-1.1 1.1M6.7 17.3l-1.1 1.1M12 8a4 4 0 100 8 4 4 0 000-8z',
  },
  {
    id: 'dark',
    icon: 'M20.5 13.2A8 8 0 1110.8 3.5a6.5 6.5 0 009.7 9.7z',
    label: 'Dark',
  },
  {
    id: 'system',
    label: 'System',
    icon: 'M4 5h16v10H4zM9 19h6M12 15v4',
  },
];

const STORAGE_KEY = 'af.theme';
const DEFAULT_MODE: ThemeMode = 'system';
const DARK_MQ = '(prefers-color-scheme: dark)';

/** Root data-attribute the CSS keys off. Light is the CSS baseline, so the
 *  attribute is only present for non-light themes and cleared otherwise. */
const THEME_ATTR = 'data-theme';

function isThemeMode(v: unknown): v is ThemeMode {
  return v === 'light' || v === 'dark' || v === 'system';
}

function systemPrefersDark(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia(DARK_MQ).matches
  );
}

/** Collapse a preference into the concrete theme to paint. */
export function resolveTheme(mode: ThemeMode): ResolvedTheme {
  if (mode === 'system') return systemPrefersDark() ? 'dark' : 'light';
  return mode;
}

export function getStoredTheme(): ThemeMode {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (isThemeMode(v)) return v;
  } catch {
    /* localStorage may be unavailable (private mode, sandbox) — fall through */
  }
  return DEFAULT_MODE;
}

function persist(mode: ThemeMode): void {
  try {
    localStorage.setItem(STORAGE_KEY, mode);
  } catch {
    /* best-effort; a non-persisting session still themes correctly this load */
  }
}

/** Stamp a resolved theme onto `<html>`. Light = CSS default, so we clear the
 *  attribute rather than set `data-theme="light"`, keeping `:root {}` as the
 *  baseline and the DOM tidy. */
function paint(resolved: ResolvedTheme): void {
  const root = document.documentElement;
  if (resolved === 'light') root.removeAttribute(THEME_ATTR);
  else root.setAttribute(THEME_ATTR, resolved);
}

/* ---- tiny external store (no Context needed) ---- */

let currentMode: ThemeMode = DEFAULT_MODE;
const listeners = new Set<() => void>();
let mql: MediaQueryList | null = null;

function emit(): void {
  listeners.forEach((l) => l());
}

function onSystemChange(): void {
  // Only relevant while tracking the OS. In an explicit light/dark mode the
  // resolved theme doesn't depend on the OS setting, so there's nothing to
  // repaint and no subscriber state that could have changed.
  if (currentMode !== 'system') return;
  paint(resolveTheme('system'));
  emit();
}

/** Keep tabs in sync: react to the preference being changed in another tab.
 *  `storage` events fire only in *other* tabs, so this never double-handles a
 *  change made in this one. */
function onStorage(e: StorageEvent): void {
  if (e.key !== STORAGE_KEY) return;
  const next = isThemeMode(e.newValue) ? e.newValue : DEFAULT_MODE;
  if (next === currentMode) return;
  currentMode = next;
  paint(resolveTheme(next));
  emit();
}

/**
 * Apply the persisted preference and start tracking the OS setting. Call once,
 * as early as possible (before React renders) so the first paint is correct and
 * there's no flash of the wrong theme.
 */
export function initTheme(): void {
  currentMode = getStoredTheme();
  paint(resolveTheme(currentMode));
  if (!mql && typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
    mql = window.matchMedia(DARK_MQ);
    mql.addEventListener('change', onSystemChange);
    window.addEventListener('storage', onStorage);
  }
}

export function getThemeMode(): ThemeMode {
  return currentMode;
}

export function setThemeMode(mode: ThemeMode): void {
  if (mode === currentMode) return;
  currentMode = mode;
  persist(mode);
  paint(resolveTheme(mode));
  emit();
}

export function subscribeTheme(cb: () => void): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

/**
 * React binding: `[mode, setMode]`. Backed by {@link subscribeTheme} so it
 * re-renders on both explicit changes and live OS-setting flips.
 */
export function useThemeMode(): [ThemeMode, (mode: ThemeMode) => void] {
  const mode = useSyncExternalStore(subscribeTheme, getThemeMode, getThemeMode);
  return [mode, setThemeMode];
}
